import React, { useState } from "react";
import { X, Star } from "lucide-react";
import { toast } from "sonner";

/**
 * Price — Modale de notation post-trajet.
 *
 * Utilisée par le passager (noter le covoitureur) et le covoitureur
 * (noter un passager). Envoie 1-5 étoiles + un commentaire optionnel.
 *
 * Props:
 *  - open         : boolean
 *  - onClose      : () => void
 *  - onSubmit     : ({stars, comment}) => Promise<void>
 *  - title        : ex. "Noter ton covoitureur"
 *  - subtitle     : ex. "Alex · Nice → Cannes"
 *  - target_label : "covoitureur" | "passager"
 */
export default function RateModal({
  open,
  onClose,
  onSubmit,
  title,
  subtitle,
  target_label = "covoitureur",
}) {
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (stars < 1 || stars > 5) {
      toast.error("Choisis une note entre 1 et 5 étoiles");
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ stars, comment: comment.trim() });
      toast.success("Merci pour ta note !");
      setStars(5);
      setComment("");
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="rate-modal"
    >
      <div
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl bg-white p-6 street-border street-shadow animate-fade-up"
        style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-[Outfit] text-[22px] font-black leading-tight">{title}</h2>
            {subtitle && (
              <p className="mt-1 text-[13px] font-medium text-foreground/60 truncate">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="street-press grid h-9 w-9 place-items-center rounded-full bg-white street-border-thin street-shadow-sm"
            aria-label="Fermer"
            data-testid="rate-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stars */}
        <div className="mt-5 flex items-center justify-center gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setStars(n)}
              className="street-press grid h-14 w-14 place-items-center rounded-2xl street-border-thin"
              style={{ background: n <= stars ? "hsl(38 95% 62%)" : "hsl(0 0% 100%)" }}
              data-testid={`rate-star-${n}`}
              aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
            >
              <Star
                className={`h-6 w-6 ${n <= stars ? "text-accent-foreground" : "text-foreground/40"}`}
                strokeWidth={2.5}
                fill={n <= stars ? "currentColor" : "none"}
              />
            </button>
          ))}
        </div>
        <div className="mt-2 text-center text-[13px] font-black text-foreground/70">
          {["", "Pas terrible", "Moyen", "Bien", "Super", "Excellent !"][stars]}
        </div>

        {/* Comment */}
        <div className="mt-4">
          <label className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
            Commentaire (optionnel)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={`Comment était ton ${target_label} ?`}
            maxLength={300}
            rows={3}
            className="mt-1 w-full resize-none rounded-2xl bg-secondary/40 p-3 text-[14px] font-medium street-border-thin focus:outline-none focus:ring-0"
            data-testid="rate-comment"
          />
          <div className="mt-1 text-right text-[10px] font-bold text-foreground/40">{comment.length}/300</div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="street-press rounded-2xl bg-white py-3 font-[Outfit] text-[14px] font-black street-border-thin street-shadow-sm disabled:opacity-60"
            data-testid="rate-skip"
          >
            Plus tard
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="street-press rounded-2xl bg-primary py-3 font-[Outfit] text-[14px] font-black text-primary-foreground street-border street-shadow disabled:opacity-60"
            data-testid="rate-submit"
          >
            {busy ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}
